import {
  type ForwardedRef,
  forwardRef,
  type HTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
  useMemo,
  useRef,
} from "react";
import * as styles from "./Panel.style.ts";
import { MiniIconButton } from "src/components/MiniIconButton/MiniIconButton.tsx";
import { composeRefs, useControlled } from "@exonn/hooks";
import { H5 } from "src/components/HeadingV2/HeadingV2.tsx";
import { PanelContext } from "src/components/Panel/PanelContext.tsx";
import { usePanelContext } from "src/components/Panel/usePanelContext.tsx";

export type PanelProps = PropsWithChildren<
  {
    expandable?: boolean;
    isLoading?: boolean;
    contentProps?: HTMLAttributes<HTMLDivElement>;
    expanded?: boolean;
    defaultExpanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
  } & HTMLAttributes<HTMLDivElement>
>;

const PanelImpl = forwardRef(
  (
    {
      isLoading = false,
      expanded: expandedProp,
      defaultExpanded = true,
      onExpandedChange,
      children,
      expandable = false,
      ...rest
    }: PanelProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [expanded, setExpanded] = useControlled({
      controlled: expandedProp,
      default: defaultExpanded,
      state: "expanded",
      name: "Panel",
      onChange: onExpandedChange,
    });
    const containerRef = useRef<HTMLDivElement | null>(null);

    const context = useMemo(
      () => ({
        expandable,
        isLoading,
        expanded: expanded || !expandable,
        setExpanded,
        containerRef,
      }),
      [expandable, isLoading, expanded, setExpanded],
    );

    return (
      <PanelContext.Provider value={context}>
        <div ref={composeRefs(ref, containerRef)} css={styles.root} {...rest}>
          {children}
        </div>
      </PanelContext.Provider>
    );
  },
);

export const PanelExpandTrigger = (
  props: HTMLAttributes<HTMLButtonElement>,
) => {
  const { ...restProps } = props;
  const { expandable, expanded } = usePanelContext();
  const handleArrowButtonClick = (e: MouseEvent) => {
    e.preventDefault(); //prevent submitting a form
  };
  if (!expandable) {
    return null;
  }
  return (
    <MiniIconButton
      onClick={handleArrowButtonClick}
      css={styles.arrowButton({ expanded })}
      {...restProps}
    >
      <i css={styles.arrow} className="icon-15-arw_dw" />
    </MiniIconButton>
  );
};

export const PanelTitle = (
  props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>,
) => {
  const { children, ...restProps } = props;
  const { isLoading } = usePanelContext();
  return (
    <H5 css={styles.title} isLoading={isLoading} {...restProps}>
      {children}
    </H5>
  );
};

export const PanelHeader = (
  props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>,
) => {
  const { children, ...restProps } = props;
  const { expandable, setExpanded, expanded } = usePanelContext();
  const handleHeaderClick = () => {
    if (expandable) {
      setExpanded(!expanded);
    }
  };
  return (
    <div
      css={styles.header({ expandable: expandable, expanded: expanded })}
      onClick={handleHeaderClick}
      {...restProps}
    >
      {children}
    </div>
  );
};

// PanelContent is deprecated, use PanelBody and PanelFields instead
export const PanelContent = (
  props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>,
) => {
  const { children, ...restProps } = props;
  const { expanded } = usePanelContext();
  if (!expanded) {
    return null;
  }

  return (
    <div css={styles.content} {...restProps}>
      {children}
    </div>
  );
};

export const PanelBody = (
  props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>,
) => {
  const { children, ...restProps } = props;
  const { expanded } = usePanelContext();
  if (!expanded) {
    return null;
  }

  return (
    <div css={styles.body} {...restProps}>
      {children}
    </div>
  );
};

export const PanelFormFields = (
  props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>,
) => {
  const { children, ...restProps } = props;

  return (
    <div css={styles.fields} {...restProps}>
      {children}
    </div>
  );
};

export const Panel = Object.assign(PanelImpl, {
  Content: PanelContent,
  Title: PanelTitle,
  Header: PanelHeader,
  ExpandTrigger: PanelExpandTrigger,
  Body: PanelBody,
  FormFields: PanelFormFields,
});
